// Component hiển thị nhóm dòng cho một bài học trong ma trận

import React from 'react'
import { Trash2 } from 'lucide-react'
import { ExamMatrixRow, DangToan } from '../../types/exam-matrix'
import { QuestionCellInput } from './QuestionCellInput'
import { calculateRowScore, countDangQuestions } from '../../utils/exam-matrix-calculator'

interface MatrixRowGroupProps {
  row: ExamMatrixRow
  rowIndex: number
  totalMatrixScore: number
  onUpdate: (id: string, updates: Partial<ExamMatrixRow>) => void
  onDelete: (id: string) => void
  onDeleteDang: (rowId: string, dangIndex: number) => void
}

export const MatrixRowGroup: React.FC<MatrixRowGroupProps> = ({
  row,
  rowIndex,
  totalMatrixScore,
  onUpdate,
  onDelete,
  onDeleteDang
}) => {
  const rowScore = calculateRowScore(row)
  const rowPercentage = totalMatrixScore > 0 ? (rowScore / totalMatrixScore) * 100 : 0
  const rowQuestions = row.cacDang.reduce((sum, d) => sum + countDangQuestions(d), 0)
  
  const handleUpdateDang = (dangIndex: number, updates: Partial<DangToan>) => {
    const newDangs = [...row.cacDang]
    newDangs[dangIndex] = { ...newDangs[dangIndex], ...updates }
    onUpdate(row.id, { cacDang: newDangs })
  }
  
  return (
    <>
      {row.cacDang.map((dang, dangIndex) => (
        <tr key={`${row.id}_${dangIndex}`} className="hover:bg-gray-50 transition-colors">
          {/* STT - merge rows */}
          {dangIndex === 0 && (
            <td rowSpan={row.cacDang.length} className="text-center border px-2 py-3 bg-gray-50 font-semibold">
              {rowIndex + 1}
            </td>
          )}
          
          {/* Chủ đề - merge rows */}
          {dangIndex === 0 && (
            <td rowSpan={row.cacDang.length} className="border px-3 py-2">
              <input
                type="text"
                value={row.chuDe}
                onChange={(e) => onUpdate(row.id, { chuDe: e.target.value })}
                className="w-full px-2 py-1.5 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="VD: Dao động"
              />
            </td>
          )}
          
          {/* Bài - merge rows */}
          {dangIndex === 0 && (
            <td rowSpan={row.cacDang.length} className="border px-3 py-2">
              <input
                type="text"
                value={row.baiHoc}
                onChange={(e) => onUpdate(row.id, { baiHoc: e.target.value })}
                className="w-full px-2 py-1.5 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="VD: Dao động điều hòa"
              />
            </td>
          )}
          
          {/* Dạng toán */}
          <td className="border px-3 py-2">
            <div className="flex items-center gap-2">
              <input
                type="text"
                value={dang.tenDang}
                onChange={(e) => handleUpdateDang(dangIndex, { tenDang: e.target.value })}
                className="flex-1 px-2 py-1.5 border rounded text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Tên dạng..."
              />
              {row.cacDang.length > 1 && (
                <button
                  onClick={() => onDeleteDang(row.id, dangIndex)}
                  className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1.5 rounded transition-colors"
                  title="Xóa dạng toán"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>
          </td>
          
          {/* TNKQ - Nhiều lựa chọn */}
          <td className="border px-2 py-2 bg-blue-50">
            <QuestionCellInput
              value={dang.tnkq.nhieuLuaChon.biet}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, nhieuLuaChon: { ...dang.tnkq.nhieuLuaChon, biet: cell }}
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-blue-50">
            <QuestionCellInput
              value={dang.tnkq.nhieuLuaChon.hieu}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, nhieuLuaChon: { ...dang.tnkq.nhieuLuaChon, hieu: cell }}
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-blue-50">
            <QuestionCellInput
              value={dang.tnkq.nhieuLuaChon.vanDung}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, nhieuLuaChon: { ...dang.tnkq.nhieuLuaChon, vanDung: cell }}
              })}
            />
          </td>
          
          {/* TNKQ - Đúng Sai */}
          <td className="border px-2 py-2 bg-green-50">
            <QuestionCellInput
              value={dang.tnkq.dungSai.biet}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, dungSai: { ...dang.tnkq.dungSai, biet: cell }}
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-green-50">
            <QuestionCellInput
              value={dang.tnkq.dungSai.hieu}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, dungSai: { ...dang.tnkq.dungSai, hieu: cell }}
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-green-50">
            <QuestionCellInput
              value={dang.tnkq.dungSai.vanDung}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, dungSai: { ...dang.tnkq.dungSai, vanDung: cell }}
              })}
            />
          </td>
          
          {/* TNKQ - Trả lời ngắn */}
          <td className="border px-2 py-2 bg-yellow-50">
            <QuestionCellInput
              value={dang.tnkq.traLoiNgan.biet}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, traLoiNgan: { ...dang.tnkq.traLoiNgan, biet: cell }}
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-yellow-50">
            <QuestionCellInput
              value={dang.tnkq.traLoiNgan.hieu}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, traLoiNgan: { ...dang.tnkq.traLoiNgan, hieu: cell }}
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-yellow-50">
            <QuestionCellInput
              value={dang.tnkq.traLoiNgan.vanDung}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tnkq: { ...dang.tnkq, traLoiNgan: { ...dang.tnkq.traLoiNgan, vanDung: cell }}
              })}
            />
          </td>
          
          {/* Tự luận */}
          <td className="border px-2 py-2 bg-purple-50">
            <QuestionCellInput
              value={dang.tuLuan.biet}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tuLuan: { ...dang.tuLuan, biet: cell }
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-purple-50">
            <QuestionCellInput
              value={dang.tuLuan.hieu}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tuLuan: { ...dang.tuLuan, hieu: cell }
              })}
            />
          </td>
          <td className="border px-2 py-2 bg-purple-50">
            <QuestionCellInput
              value={dang.tuLuan.vanDung}
              onChange={(cell) => handleUpdateDang(dangIndex, {
                tuLuan: { ...dang.tuLuan, vanDung: cell }
              })}
            />
          </td>
          
          {/* Tổng - chỉ hiển thị ở dòng đầu tiên */}
          {dangIndex === 0 && (
            <>
              <td rowSpan={row.cacDang.length} className="text-center font-bold bg-indigo-50 border px-2 py-2">
                {rowQuestions}
              </td>
              <td rowSpan={row.cacDang.length} className="text-center font-bold bg-indigo-50 border px-2 py-2">
                {rowPercentage.toFixed(1)}%
              </td>
            </>
          )}
        </tr>
      ))}
    </>
  )
}

